import config from "@/config";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { clearLocalStorage, getSession, setSession } from "../utils/auth";
import { readStringFlag } from "../utils/localFlag";
import { notificate } from "../utils/message";
import { clearAllResponseMemos } from "../utils/responseMemo";
import { refresh } from "./security";

let prepareRequest = axios.create({
    baseURL: config.baseApi,
    timeout: 30000,
    headers: { "Content-Type": "application/json" },
});

export const requestWithoutToken = prepareRequest;

// Instancia para postear DIRECTO al bucket S3 con un formulario prefirmado
// (decisiones §35): sin baseURL, sin JWT ni interceptores (S3
// rechazaría el header Authorization y un 403 suyo no es "sesión vencida"), y
// sin tope de tiempo — el archivo puede pesar decenas de MB en un enlace lento.
export const requestToS3 = axios.create({ timeout: 0 });

/**
 * Sube `file` a S3 con el formulario prefirmado que emitió el backend.
 * @param {string} url - URL del bucket (`url` de la respuesta de upload_url).
 * @param {Object} fields - Campos firmados (`fields`); van ANTES del archivo,
 *   S3 ignora lo que venga después de `file`.
 * @param {File} file
 * @param {Function} [onUploadProgress] - Callback de axios con {loaded, total}.
 * @returns {Promise<AxiosResponse>} 204 de S3; rechaza si la firma expiró,
 *   el tamaño/tipo no calzan con la política o el bucket no tiene CORS.
 */
export function s3Upload(url, fields, file, onUploadProgress) {
    const formData = new FormData();
    Object.entries(fields).forEach(([name, value]) => formData.append(name, value));
    formData.append("file", file);
    return requestToS3.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
    });
}

/**
 * Uploads a file to the backend using the provided endpoint.
 * @param {string} url - The API endpoint for file upload.
 * @param {File} file - The file to be uploaded.
 * @param {string} fieldName - The name of the form field to send the file under. Default is "file".
 * @returns {Promise} - Resolves with the API response or rejects with an error.
 */
export async function uploadFile(url, file, fieldName = "file") {
    const formData = new FormData();
    formData.append(fieldName, file);

    try {
        const response = await requestWithToken.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response;
    } catch (error) {
        console.error("File upload failed:", error);
        throw error;
    }
}

// Avisos de sesión. Que el backend invalide el token (deploy, cambio de
// permisos desde el panel, conexión SII) o que venza en el cliente NO es una
// falla: la app se protege cerrando o renovando la sesión. Un solo toast
// informativo (verde si la sesión se renovó, naranja si va al login), una
// pausa para que se alcance a leer, y el error queda marcado
// (`isAuthRedirect`) para que consoleHandlerError no pinte el rojo genérico
// encima — hasta ahora el usuario veía "estamos teniendo un pequeño error".
export const AUTH_REDIRECT_DELAY_MS = 1500;
export const AUTH_NOTICES = {
    refreshing: { message: "Actualizando...", type: "is-success is-light" },
    toLogin: {
        message: "Por seguridad te estamos redirigiendo al login",
        type: "is-warning is-light",
    },
};

// Single-shot: N requests en vuelo que fallan juntas avisan una sola vez y
// programan una sola redirección.
let authRedirectScheduled = false;

function redirectWithNotice(redirectTo, notice) {
    if (authRedirectScheduled) return;
    authRedirectScheduled = true;
    notificate(`<p class="m-0">${notice.message}</p>`, notice.type);
    setTimeout(() => {
        window.location.href = redirectTo;
    }, AUTH_REDIRECT_DELAY_MS);
}

function markAuthRedirect(error) {
    if (error && typeof error === "object") {
        error.isAuthRedirect = true;
    }
    return error;
}

prepareRequest.interceptors.request.use(
    (config) => {
        let session = getSession();
        if (session) {
            // get token
            let token = "";
            let diff = 0;
            let error = false;
            try {
                // get time diff
                const timeDiff = Number(
                    localStorage.getItem("time_difference")
                );
                // get local time
                const localDate = new Date();
                const localTime = localDate.getTime();
                // get expiration time
                let expDate = new Date(0);
                token = jwt_decode(session.token);
                expDate.setUTCSeconds(token.exp);
                let expTime = expDate.getTime();
                if (timeDiff > 0) {
                    expTime = expTime - timeDiff;
                } else {
                    expTime = expTime + Math.abs(timeDiff);
                }
                // set countdown time
                diff = expTime - localTime;
            } catch (err) {
                error = true;
            }
            if (diff <= 0 || error) {
                // Token vencido/ilegible: NO mandar el request (dispararía un
                // 401 y otro redirect encima del que ya está en curso). La
                // cancelación ya la ignora consoleHandlerError.
                clearLocalStorage();
                redirectWithNotice("/", AUTH_NOTICES.toLogin);
                return Promise.reject(
                    // Forma axios 1.x (axios.Cancel quedó como alias legacy).
                    new axios.CanceledError(
                        "Sesión expirada: redirigiendo al login."
                    )
                );
            }
            config.headers["Authorization"] = `Bearer ${session.token}`;
            // Scope the request to the user's selected company. Read straight
            // from localStorage (not the store) to avoid an api<->store import
            // cycle; switching company persists here + reloads, so the value
            // is always consistent at request time. The backend validates it
            // against the user's companies (get_company_user).
            const activeCompanyId = readStringFlag("activeCompanyId");
            if (activeCompanyId) {
                config.headers["X-Active-Company"] = activeCompanyId;
            }
        }
        return config;
    },
    (error) => {
        // Sin el return, el error de request se tragaba (la promise del
        // caller quedaba resuelta con undefined en vez de rechazada).
        return Promise.reject(error);
    }
);

// Refresh single-flight: N requests concurrentes que reciben 403 disparaban
// N refresh() y N redirects pisándose entre sí. El primero arma la promise;
// los demás se cuelgan de ella.
let refreshInFlight = null;

// declare a response interceptor
prepareRequest.interceptors.response.use(
    (response) => {
        // Cualquier escritura desde este navegador puede cambiar el reporte
        // financiero: vaciar los memos de respuestas GET (utils/responseMemo).
        const method = (response?.config?.method || "get").toLowerCase();
        if (method !== "get") {
            clearAllResponseMemos();
        }
        return response;
    },
    (error) => {
        const status = error?.response?.status;
        const url = error?.response?.config?.url;
        if (
            403 === status &&
            !error.response.config.skipAuthRedirect &&
            url !== "/logout/"
        ) {
            // 403 con sesión = token invalidado por el backend ("Token is
            // blacklisted"). Se intenta renovar: si el refresh anda, la
            // sesión sigue con permisos frescos ("Actualizando..."); si no,
            // se cierra por seguridad y se va al login.
            if (!refreshInFlight) {
                const session = getSession();
                refreshInFlight = (
                    session && session.token
                        ? refresh(session.token)
                              .then((res) => {
                                  setSession(res.data);
                                  return "/go-to-plan";
                              })
                              .catch(() => {
                                  clearLocalStorage();
                                  return "/";
                              })
                        : Promise.resolve("/")
                ).then((redirectTo) => {
                    redirectWithNotice(
                        redirectTo,
                        redirectTo === "/"
                            ? AUTH_NOTICES.toLogin
                            : AUTH_NOTICES.refreshing
                    );
                });
            }
            // Rechazar SIEMPRE hacia el caller (antes esta rama dejaba la
            // promise sin resolver: los catch/finally de las actions nunca
            // corrían y los loading flags quedaban colgados).
            return refreshInFlight.then(() =>
                Promise.reject(markAuthRedirect(error))
            );
        }
        if (403 === status && url === "/logout/") {
            // El logout ya va al login por su cuenta: sin aviso.
            clearLocalStorage();
            window.location.href = "/";
        } else if (401 === status) {
            clearLocalStorage();
            redirectWithNotice("/", AUTH_NOTICES.toLogin);
            markAuthRedirect(error);
        }
        return Promise.reject(error);
    }
);

export const requestWithToken = prepareRequest;
